import type {
  MotionTokens,
  RadiusTokens,
  SpacingTokens,
  Theme,
  TypographyTokens,
} from '../../theme/contract';

/**
 * Cozy Pastel — the family/kid theme. Token values are extracted from
 * `docs/mockups/02-cozy-pastel.html`: soft lavender primary, mint + peach accents, warm off-white
 * canvas, generous radii, Quicksand display font, gentle shadows.
 *
 * Non-color tokens (radii/spacing/typography/motion) are shared between light and dark; only the
 * color set differs.
 */

const radius: RadiusTokens = {
  sm: '14px',
  md: '20px',
  lg: '24px',
  card: '24px',
  pill: '999px',
};

const spacing: SpacingTokens = {
  '1': '4px',
  '2': '8px',
  '3': '12px',
  '4': '16px',
  '5': '20px',
  '6': '24px',
  '7': '32px',
};

const typography: TypographyTokens = {
  fontSans: "'Quicksand', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
  fontDisplay: "'Quicksand', system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
  fontMono: "ui-monospace, 'SFMono-Regular', 'Menlo', monospace",
  sizeXs: '11px',
  sizeSm: '12px',
  sizeBase: '14px',
  sizeMd: '16px',
  sizeLg: '19px',
  sizeXl: '24px',
  sizeXxl: '30px',
  weightNormal: '400',
  weightMedium: '500',
  weightSemibold: '600',
  weightBold: '700',
  trackingTight: '-0.5px',
  leadingTight: '1.1',
  leadingNormal: '1.6',
};

const motion: MotionTokens = {
  durationFast: '120ms',
  durationNormal: '220ms',
  durationSlow: '420ms',
  easeStandard: 'cubic-bezier(0.4, 0, 0.2, 1)',
  easeBounce: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
};

export const cozyPastel: Theme = {
  id: 'cozy-pastel',
  light: {
    color: {
      bg: '#FAF7F5',
      surface: '#FFFFFF',
      surfaceMuted: '#F4F0F8',
      // Darkened from the soft lavender `#C3B1E1` so it clears WCAG AA (≥4.5:1) as accent INK on the
      // light surfaces (pills, KPI figures, links): 5.52:1 on surface, 4.90:1 on surface-muted. As a
      // brand FILL it now pairs with a white foreground (`primaryFg`), 5.52:1.
      primary: '#7458B5',
      primaryFg: '#FFFFFF',
      secondary: '#B8E0D2',
      secondaryFg: '#2E5547',
      accent: '#FFD9C0',
      accentFg: '#8A4F2E',
      text: '#4A4458',
      textMuted: '#7A7388',
      border: '#EEE8F2',
      input: '#E4DCEC',
      // Darkened ink so the success pill (`text-success` on the soft `success-fg`) clears AA: 5.09:1
      // on `success-fg`, 5.86:1 on surface. As a fill (CompleteButton done) it pairs with `success-fg`.
      success: '#2A7152',
      successFg: '#E5F2EC',
      // Darkened ink so the warning pill clears AA: 5.19:1 on `warning-fg`, 5.91:1 on surface.
      warning: '#8A5A12',
      warningFg: '#FBEFD7',
      danger: '#D87B7B',
      dangerFg: '#FFFFFF',
      // Effort-stars (✨) = violet — the third currency, distinct from coins (gold) / cash (green).
      star: '#6C4FB0',
      starFg: '#ECE5F7',
      ring: '#7458B5',
    },
    // Soft-but-distinct pastels: lavender → mint → peach → rose → sky → gold. Six hues kept far enough
    // apart to read as separate categories without going loud.
    chart: {
      '1': '#7458B5',
      '2': '#3E9D7B',
      '3': '#C9772E',
      '4': '#D87B7B',
      '5': '#5E8C9E',
      '6': '#B6892E',
      grid: '#EEE8F2',
      axis: '#7A7388',
      cursor: '#F4F0F8',
    },
    radius,
    spacing,
    typography,
    shadow: {
      sm: '0 2px 8px rgba(74,68,88,.05), 0 1px 2px rgba(74,68,88,.04)',
      md: '0 8px 22px rgba(74,68,88,.07), 0 2px 6px rgba(74,68,88,.05)',
      lg: '0 14px 38px rgba(74,68,88,.10), 0 4px 10px rgba(74,68,88,.06)',
      card: '0 8px 22px rgba(74,68,88,.07), 0 2px 6px rgba(74,68,88,.05)',
      nav: '0 -6px 20px rgba(74,68,88,.05)',
    },
    motion,
  },
  dark: {
    color: {
      bg: '#211E2B',
      surface: '#2B2738',
      surfaceMuted: '#332E42',
      primary: '#C3B1E1',
      primaryFg: '#231F30',
      secondary: '#8FC9B6',
      secondaryFg: '#13241E',
      accent: '#F0B894',
      accentFg: '#2B1A0F',
      text: '#EFEAF5',
      textMuted: '#B0A8C0',
      border: '#3C3650',
      input: '#453E5C',
      success: '#7CC4A1',
      successFg: '#1B3329',
      warning: '#E0B36A',
      warningFg: '#352915',
      danger: '#E59494',
      dangerFg: '#2B1414',
      star: '#C3B1E1',
      starFg: '#272036',
      ring: '#C3B1E1',
    },
    chart: {
      '1': '#C3B1E1',
      '2': '#8FC9B6',
      '3': '#F0B894',
      '4': '#E59494',
      '5': '#93B7C6',
      '6': '#D9BE7A',
      grid: '#3C3650',
      axis: '#B0A8C0',
      cursor: '#332E42',
    },
    radius,
    spacing,
    typography,
    shadow: {
      sm: '0 2px 8px rgba(0,0,0,.30), 0 1px 2px rgba(0,0,0,.24)',
      md: '0 8px 22px rgba(0,0,0,.40), 0 2px 6px rgba(0,0,0,.30)',
      lg: '0 14px 38px rgba(0,0,0,.50), 0 4px 10px rgba(0,0,0,.34)',
      card: '0 8px 22px rgba(0,0,0,.40), 0 2px 6px rgba(0,0,0,.30)',
      nav: '0 -6px 20px rgba(0,0,0,.40)',
    },
    motion,
  },
};
